<template>
  <div class="menu-footer">
    <div class="menu-footer__controls">
      <div
        class="menu-footer__pill"
        :class="{ active: isLimitsPanelOpen }"
        @click="isLimitsPanelOpen = !isLimitsPanelOpen"
      >
        <span class="menu-footer__pill-label">{{ $t('Max') }}</span>
        <span class="menu-footer__pill-divider">|</span>
        <span class="menu-footer__pill-value">{{ formatAmountWithKM(mainLimitAmountPerFight, 0) }}</span>
        <span class="menu-footer__chevron" :class="{ up: isLimitsPanelOpen }"></span>
        <div class="drop-down">
          <div class="drop-down-item">
            <img src="@/assets/svg/chart-icons/banker.svg" alt="Banker" />
            <img src="@/assets/svg/chart-icons/player.svg" alt="Player" />
            <span class="limit-text">{{ formatAmountWithKM(mainLimitAmountPerFight, 0) }}</span>
          </div>
          <div class="drop-down-item">
            <img class="draw-icon" src="@/assets/svg/chart-icons/draw.svg" alt="Draw" />
            <span class="limit-text">{{ formatAmountWithKM(drawLimitAmountPerFight, 0) }}</span>
          </div>
        </div>
      </div>

      <div class="menu-footer__pill menu-footer__lang">
        <LanguageDropdown :open="'up'" />
      </div>

      <div class="menu-footer__float-group">
        <button
          type="button"
          class="menu-footer__icon-btn"
          :class="{ active: isFloatingVideo }"
          :title="$t('float video')"
          :aria-pressed="String(isFloatingVideo)"
          @click="updateFloatVideo(true)"
        >
          <span class="menu-footer__float-icon menu-footer__float-icon--on"></span>
        </button>
        <span class="menu-footer__float-divider" aria-hidden="true"></span>
        <button
          type="button"
          class="menu-footer__icon-btn"
          :class="{ active: !isFloatingVideo }"
          :title="$t('float video')"
          :aria-pressed="String(!isFloatingVideo)"
          @click="updateFloatVideo(false)"
        >
          <span class="menu-footer__float-icon menu-footer__float-icon--off"></span>
        </button>
      </div>

      <button type="button" class="menu-footer__close" aria-label="Close" @click="$emit('close')">
        <img src="@/assets/images/close.webp" alt="" />
      </button>
    </div>

    <div class="menu-footer__user-row">
      <!-- Balance first and on the left, where the closed footer panel keeps it. -->
      <div class="menu-footer__balance">{{ currencySymbol }} {{ userBalance }}</div>
      <div class="menu-footer__user">
        <img src="@/assets/images/user.webp" alt="" />
        <span>{{ memberName }}</span>
      </div>
      <div class="menu-footer__logo-wrap">
        <Logo />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatAmount, formatAmountWithKM } from '@/utils/helpers';
import LanguageDropdown from '@/components/menus/language-dropdown';
import Logo from '@/components/logo.vue';

export default {
  components: {
    LanguageDropdown,
    Logo,
  },
  data: () => ({
    isLimitsPanelOpen: false,
  }),
  computed: {
    ...mapGetters({
      balance: 'general/realBalance',
      config: 'general/config',
      member: 'general/member',
      selectedArena: 'general/selectedArena',
    }),
    isFloatingVideo() {
      return this.config.floatVideo;
    },
    userBalance() {
      return formatAmount(this.balance, 2);
    },
    currencySymbol() {
      return this.member.currencySymbol;
    },
    memberName() {
      const name = this.member.displayName;
      return name.length > 14 ? `${name.substr(0, 14)}...` : name;
    },
    mainLimitAmountPerFight() {
      if (this.selectedArena && this.selectedArena.mainLimitAmountPerFight) {
        return Number(this.selectedArena.mainLimitAmountPerFight) < Number(this.config.mainLimitAmountPerFight)
          ? Number(this.selectedArena.mainLimitAmountPerFight)
          : Number(this.config.mainLimitAmountPerFight);
      }
      return Number(this.config.mainLimitAmountPerFight);
    },
    drawLimitAmountPerFight() {
      if (this.selectedArena && this.selectedArena.drawLimitAmountPerFight) {
        return Number(this.selectedArena.drawLimitAmountPerFight) < Number(this.config.drawLimitAmountPerFight)
          ? Number(this.selectedArena.drawLimitAmountPerFight)
          : Number(this.config.drawLimitAmountPerFight);
      }
      return Number(this.config.drawLimitAmountPerFight);
    },
  },
  methods: {
    formatAmountWithKM,
    ...mapActions('general', ['updateFloatVideo']),
  },
};
</script>

<style lang="scss" scoped>
.menu-footer {
  flex-shrink: 0;
  width: 100%;
  // Clear of the home indicator where the browser reports one.
  padding: 8px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  background-color: #07182c;
  border-top: 1px solid rgba(106, 145, 167, 0.35);
  position: relative;
  z-index: 2;

  &__controls {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: nowrap;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    height: 27px;
    padding: 0 10px;
    border: 1px solid #6a91a6;
    border-radius: 14px;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #fff;
    white-space: nowrap;
    flex-shrink: 0;

    &-label {
      color: #6a91a7;
    }

    &-divider {
      color: #6a91a7;
      margin: 0 4px;
    }

    &-value {
      color: #fff;
    }

    @include dropDown;
    .drop-down {
      bottom: 100%;
      top: unset;
      border-radius: 8px;
      margin-bottom: 4px;
      z-index: 10;
    }

    &.active .drop-down,
    &:hover .drop-down {
      display: block;
    }
  }

  &__chevron {
    display: block;
    width: 6px;
    height: 6px;
    margin-left: 4px;
    transform: scale(0.6) rotate(-45deg);
    border: 2px solid #fff;
    border-bottom: none;
    border-left: none;
    transition: transform 0.15s ease;

    &.up {
      transform: scale(0.6) rotate(135deg);
    }
  }

  /*
   * No max-width: the pill's content (flag + name + chevron) is ~140px, and
   * capping the box at 110px let it spill over the float-video icons. On a
   * phone the name goes instead -- see the media query below.
   */
  &__lang {
    padding: 0;
    border: none;
    height: auto;
    cursor: default;

    ::v-deep .dropdowns-container {
      width: auto;
    }

    ::v-deep .dropdowns__item__wrapper__content {
      border: 1px solid #6a91a6;
      border-radius: 14px;
      height: 27px;
      padding: 0 10px;
    }

    ::v-deep .txt {
      font-size: 12px;
      font-weight: 600;
      min-width: auto;
      margin-left: 8px;
    }
  }

  // Float video is a pair of bare icons either side of a divider: the state's
  // own icon is white, the other #B2D6EA.
  &__float-group {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    -webkit-tap-highlight-color: transparent;
  }

  &__float-divider {
    width: 1px;
    height: 20px;
    background: #6a91a6;
  }

  /*
   * Masked rather than <img>, so one file serves both colours: the current
   * state is white, the other is dimmed well back so the two read apart at a
   * glance -- #B2D6EA was close enough to white to look like two active icons.
   */
  &__float-icon {
    width: 24px;
    height: 24px;
    display: block;
    background-color: rgba(178, 214, 234, 0.38);
    transition: background-color 0.15s ease;

    &--on {
      -webkit-mask: url('~@/assets/svg/float-video-on.svg') no-repeat center / contain;
      mask: url('~@/assets/svg/float-video-on.svg') no-repeat center / contain;
    }

    &--off {
      -webkit-mask: url('~@/assets/svg/float-video-off.svg') no-repeat center / contain;
      mask: url('~@/assets/svg/float-video-off.svg') no-repeat center / contain;
    }
  }

  &__icon-btn.active &__float-icon {
    background-color: #fff;
  }

  &__close {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 50%;
    background: linear-gradient(358deg, #bcbfc3 99%, #f0f0f0 1%);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0;

    img {
      width: 14px;
      height: 14px;
    }
  }

  /*
   * Phones (an iPhone 15 Pro is 393px): MAX, language, both float icons and
   * close cannot all fit at full size in ~360px. The language pill drops to
   * its flag and chevron -- the open list still names every language -- and
   * close comes down to 32px, which leaves the float icons room either side
   * of their divider.
   */
  @media (max-width: 430px) {
    &__lang {
      ::v-deep .dropdowns__item__wrapper__content {
        padding: 0 8px;
      }

      ::v-deep .txt {
        min-width: 0;
        margin-left: 2px;
        font-size: 0;
      }
    }

    &__close {
      width: 32px;
      height: 32px;
    }
  }

  &__user-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 20px;
    overflow: hidden;
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #fff;
    min-width: 0;
    flex: 1;
    justify-content: flex-end;

    img {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__balance {
    // The closed panel indents its amount 4px; same here so it does not shift.
    margin-left: 4px;
    font-size: 14px;
    font-weight: 700;
    color: #efc87f;
    font-feature-settings: 'tnum' on, 'lnum' on;
    white-space: nowrap;
    flex-shrink: 0;
    max-width: 45%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__logo-wrap {
    margin-left: auto;
    flex-shrink: 0;
    height: 16px;
    display: flex;
    align-items: center;
    overflow: hidden;

    ::v-deep .logo {
      display: block;
      height: 16px;
      width: auto;
      max-height: 16px;
    }
  }
}
</style>
